<template>
  <div class="builder-side-bar">
    <div class="sticky-top">
      <!-- LOGIGRAM OPTION -->
      <LogigramOption
        v-if="rootId && displayOptions.logigram && editable && !isTemplate"
        :diagnostic-id="rootId"
        :type="type"
        :user-permissions="userPermissions"
        @relocate-child="$emit('relocate-child', $event)"
      />

      <!-- IMPORTED ARTICLES -->
      <ImportedArticlesOption
        v-if="displayOptions.import && editable"
        :is-read-only="content.isReadOnly"
        :content-id="content.id"
        :type="type"
      />

      <!-- KEY STEP -->
      <KeyStepOption
        v-if="displayOptions.keyStep && editable"
        :type="type"
        :is-key-step="isKeyStep"
        @click="$emit('toggle-key-step', $event)"
      />

      <AiDrawerOption
        v-if="editable && displayAiDrawerOptions && type !== 'diagnostic'"
        :type="type"
        :generating-summary="generatingSummary"
        :generating-action-items="generatingActionItems"
        :generating-guide="generatingGuide"
        :summary="content.summary"
        :short-summary="content.shortSummary"
        :action-items="content.actionItems"
        :ai-drawer-tab="aiDrawerTab"
        :body-length="content.body.length"
        :minimum-content-size="minimumContentSize"
        @generate-content="$emit('generate-content', $event)"
        @generate-guide="$emit('generate-guide')"
      />

      <!-- LABELS EDITOR -->
      <LabelsOption
        v-if="displayOptions.labels && editable"
        :tags="content.tags"
        :type="type"
        :user-permissions="userPermissions"
        @update-content="$emit('update-content', $event)"
      />

      <!-- TRIGGER OPTION -->
      <TriggerOption
        v-if="displayOptions.trigger && editable"
        :type="type"
        :active="triggerActive"
        @click="$emit('click:trigger', $event)"
      />

      <HistorizationOption
        v-if="showHistorization"
        :type="type"
        @click="$emit('click:historization', payload)"
      />

      <!-- NOTIFICATIONS -->
      <NotificationsOption
        v-if="displayOptions.notifications && editable"
        :node-id="nodeId"
        @add-notification="$emit('add-notification', $event)"
        :type="content.type"
      />

      <!-- AUTOMATIONS OPTION -->
      <AutomationsOption
        v-if="
          displayOptions.automations &&
          (hasIntercom || hasZendesk || hasSalesforce) &&
          editable
        "
        :type="type"
        :knowledge-id="knowledgeId"
        :node-id="nodeId"
        :automations="automations"
        :new-automation-id="newAutomationId"
        :user-permissions="userPermissions"
        @create-automation="$emit('create-automation', $event)"
        @update-automation="$emit('update-automation', $event)"
        @delete-automation="$emit('delete-automation', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { sideBarDisplayOptions } from 'utils/displayOptions';

import LogigramOption from './LogigramOption';
import ImportedArticlesOption from './ImportedArticlesOption';
import LabelsOption from './LabelsOption';
import KeyStepOption from './KeyStepOption';
import TriggerOption from './TriggerOption';
import HistorizationOption from './HistorizationOption';
import NotificationsOption from './NotificationsOption';
import AutomationsOption from './AutomationsOption/AutomationsOption';
import AiDrawerOption from 'views/KnowledgeEditor/ParametricContentBuilder/ParametricBuilderSibeBar/AiDrawerOption.vue';

export default {
  name: 'builder-side-bar',
  components: {
    AiDrawerOption,
    LogigramOption,
    ImportedArticlesOption,
    LabelsOption,
    KeyStepOption,
    TriggerOption,
    NotificationsOption,
    AutomationsOption,
    HistorizationOption,
  },
  props: {
    type: {
      type: String,
      default: () => 'article',
    },
    knowledgeId: String,
    rootId: {
      type: String,
    },
    nodeId: {
      type: String,
      required: true,
    },
    isKeyStep: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    content: {
      type: Object,
      required: true,
    },
    isPublished: {
      type: Boolean,
      default: false,
    },
    automations: Array,
    newAutomationId: {
      type: String,
      required: false,
    },
    attachedTreeNodes: Array,
    editable: {
      type: Boolean,
      default: true,
    },
    triggerActive: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
    generatingSummary: {
      type: Boolean,
      default: false,
    },
    generatingShortSummary: {
      type: Boolean,
      default: false,
    },
    generatingActionItems: {
      type: Boolean,
      default: false,
    },
    generatingGuide: {
      type: Boolean,
      default: false,
    },
    aiDrawerTab: {
      type: String,
      default: null,
    },
    minimumContentSize: {
      type: Number,
      default: 300,
    },
  },
  computed: {
    isFirst() {
      return this.content.type === 'Diagnostic';
    },
    displayOptions() {
      return sideBarDisplayOptions({
        contentType: this.type,
        isFirst: this.isFirst,
        isKeyStep: this.isKeyStep,
        isPublished: this.isPublished,
        hasRestriction: this.hasAgentRestrictions,
        isParametric: this.isParametric,
      });
    },
    hasPayload() {
      return this.payload && !!this.payload.symptom;
    },
    payload() {
      const html = new DOMParser().parseFromString(
        this.content.body,
        'text/html',
      ).documentElement;
      const body = html.getElementsByTagName('body');
      const children = Array.from(body)[0].children;

      const symptomIdx = this.extractTextIdx(children, 'Problème');
      const diagnosticIdx = this.extractTextIdx(children, 'Cause');
      const resolutionIdx = this.extractTextIdx(children, 'Solution');
      const compIdx = this.extractTextIdx(
        children,
        'Informations complémentaires',
      );

      return {
        symptom: this.extractInnerText(children, symptomIdx, diagnosticIdx),
        diagnose: this.extractInnerText(children, diagnosticIdx, resolutionIdx),
        resolution: this.extractInnerText(children, resolutionIdx, compIdx),
      };
    },
    showHistorization() {
      return this.session && !this.editable && this.hasPayload;
    },
    displayAiDrawerOptions() {
      return (
        this.companyAllowActionItemsPreference ||
        this.companyAllowSummaryPreference ||
        this.companyAllowDecisionTreePreference
      );
    },
    ...mapState(['session']),
    ...mapGetters([
      'hasZendesk',
      'hasIntercom',
      'hasSalesforce',
      'hasAgentRestrictions',
    ]),
    ...mapGetters([
      'isParametric',
      'companyAllowSummaryPreference',
      'companyAllowActionItemsPreference',
      'companyAllowDecisionTreePreference',
    ]),
  },
  methods: {
    updateContent(payload) {
      this.$emit('update-content', {
        id: this.content.id,
        payload,
      });
    },
    extractTextIdx(htmlCollection, text) {
      if (!htmlCollection) return -1;
      return Array.from(htmlCollection).findIndex((el) => {
        if (el.tagName != 'H2') return false;
        return el.innerText.includes(text);
      });
    },
    extractInnerText(htmlCollection, startIdx, endIdx) {
      if (!htmlCollection || startIdx === -1) return '';
      return Array.from(htmlCollection)
        .slice(startIdx + 1, endIdx)
        .map((el) => el.innerText)
        .join(' ');
    },
  },
};
</script>

<style lang="scss" scoped>
.builder-side-bar {
  width: 60px;
  z-index: 2;
}
</style>
